import {
  BUSINESS_TYPE_LABELS,
  BUSINESS_TYPES,
  openingHoursSchema,
  updateLocalSchema,
  type UpdateHoursInput,
  type UpdateLocalInput,
} from '@gl/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { FormField } from '@/components/FormField';
import { Spinner } from '@/components/FullScreenLoader';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { useCurrentLocal, useHasRole } from '@/lib/local-context';
import { HoursEditor, hoursToState, stateToHours, type DayState } from './HoursEditor';
import { useLocalMutation } from './useUpdateLocal';

const START_HOURS = Array.from({ length: 13 }, (_, h) => h);

export function GeneralPage() {
  const local = useCurrentLocal();
  const isOwner = useHasRole('OWNER');
  const canEditHours = useHasRole('OWNER', 'MANAGER');

  // ---- Datos del local ----
  const form = useForm({
    resolver: zodResolver(updateLocalSchema),
    defaultValues: {
      name: local.name,
      description: local.description ?? '',
      address: local.address ?? '',
      whatsapp: local.whatsapp ?? '',
      instagram: local.instagram ?? '',
      businessType: local.businessType,
      businessDayStartHour: local.businessDayStartHour,
    },
  });
  const { errors, isDirty } = form.formState;
  const update = useLocalMutation((body: UpdateLocalInput) => api.locals.update(local.id, body));

  // ---- Horarios ----
  const [hours, setHours] = useState<DayState[]>(() => hoursToState(local.openingHours));
  const [hoursDirty, setHoursDirty] = useState(false);
  const updateHours = useLocalMutation(
    (body: UpdateHoursInput) => api.locals.updateHours(local.id, body),
    'Horarios guardados',
  );

  const saveHours = () => {
    const payload = stateToHours(hours);
    const parsed = openingHoursSchema.safeParse(payload);
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? 'Revisá los horarios');
      return;
    }
    updateHours.mutate({ openingHours: parsed.data }, { onSuccess: () => setHoursDirty(false) });
  };

  return (
    <div className="grid gap-6">
      <Card>
        <CardHeader>
          <CardTitle>Datos del local</CardTitle>
          <CardDescription>Se muestran en tu menú público.</CardDescription>
        </CardHeader>
        <form
          onSubmit={form.handleSubmit((v) =>
            update.mutate(v, { onSuccess: (l) => form.reset({ ...v, name: l.name }) }),
          )}
          noValidate
        >
          <fieldset disabled={!isOwner} className="contents">
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <FormField label="Nombre" htmlFor="name" error={errors.name?.message} className="sm:col-span-2">
                <Input id="name" {...form.register('name')} />
              </FormField>
              <FormField
                label="Descripción"
                htmlFor="description"
                error={errors.description?.message}
                className="sm:col-span-2"
              >
                <Textarea id="description" rows={3} placeholder="Helado artesanal desde 1998…" {...form.register('description')} />
              </FormField>
              <FormField label="Dirección" htmlFor="address" error={errors.address?.message} className="sm:col-span-2">
                <Input id="address" placeholder="Av. Siempre Viva 742, CABA" {...form.register('address')} />
              </FormField>
              <FormField label="WhatsApp" htmlFor="whatsapp" error={errors.whatsapp?.message} hint="Con código de país, p. ej. +54 9 11 1234-5678">
                <Input id="whatsapp" inputMode="tel" {...form.register('whatsapp')} />
              </FormField>
              <FormField label="Instagram" htmlFor="instagram" error={errors.instagram?.message} hint="Solo el usuario, sin @">
                <Input id="instagram" autoCapitalize="none" {...form.register('instagram')} />
              </FormField>
              <FormField label="Rubro">
                <Controller
                  control={form.control}
                  name="businessType"
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange} disabled={!isOwner}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {BUSINESS_TYPES.map((t) => (
                          <SelectItem key={t} value={t}>
                            {BUSINESS_TYPE_LABELS[t]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </FormField>
              <FormField label="El día de caja arranca a las" hint="Las ventas antes de esta hora cuentan para el día anterior.">
                <Controller
                  control={form.control}
                  name="businessDayStartHour"
                  render={({ field }) => (
                    <Select
                      value={String(field.value ?? 6)}
                      onValueChange={(v) => field.onChange(Number(v))}
                      disabled={!isOwner}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {START_HOURS.map((h) => (
                          <SelectItem key={h} value={String(h)}>
                            {String(h).padStart(2, '0')}:00
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </FormField>
            </CardContent>
          </fieldset>
          <CardFooter className="mt-5 justify-end">
            {!isOwner && <p className="mr-auto text-xs text-muted-foreground">Solo el dueño puede editar estos datos.</p>}
            {isOwner && (
              <Button type="submit" disabled={!isDirty || update.isPending}>
                {update.isPending && <Spinner />} Guardar
              </Button>
            )}
          </CardFooter>
        </form>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Horarios</CardTitle>
          <CardDescription>
            Tu menú muestra si estás abierto o cerrado. Si el cierre es antes que la apertura, el horario cruza la medianoche.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <HoursEditor
            value={hours}
            disabled={!canEditHours}
            onChange={(next) => {
              setHours(next);
              setHoursDirty(true);
            }}
          />
        </CardContent>
        {canEditHours && (
          <CardFooter className="justify-end">
            <Button onClick={saveHours} disabled={!hoursDirty || updateHours.isPending}>
              {updateHours.isPending && <Spinner />} Guardar horarios
            </Button>
          </CardFooter>
        )}
      </Card>
    </div>
  );
}
